import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Skeleton } from '@mui/material';

import { useUnumApplicationQuery } from 'enrollment/queries';
import { BenefitPlansDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import { selectEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import { InternalPage } from 'theSource/components';
import { BackLink } from 'enrollment/components';
import { UnumApplicationQuestions } from '../UnumApplicationQuestions';
import { UnumApplicationDocuments } from './UnumApplicationDocuments';
import { UnumApplicationStatement } from './UnumApplicationStatement';
import { UnumApplicationSignatures } from './UnumApplicationSignatures';
import {
  ErrorResponseDto,
  UnumApplicationRequestDto,
  UnumApplicationSurveyResponsesDto,
  UnumExitAppResponseDto,
} from '../types';
import { PsButtonTuner } from 'stencilSetup';
import { initalizeResponses } from '../UnumApplicationQuestions/utils';
import {
  formatApplicationBeneficiariesPayload,
  formatApplicationDocumentsPayload,
  formatApplicationOptionsPayload,
  formatApplicationQuestionPayload,
  formatApplicationSignedPayload,
  formatHeightQuestionPayload,
  PUT_UNUM_SUBMIT_APPLICATION,
} from '../utils';
import { UnumApplicationBeneficiaries } from './UnumApplicationBeneficiaries';
import { UnumApplicationOptions } from './UnumApplicationOptions';
import { BeneficiariesDto } from 'enrollment/queries/useEnrollmentAddBeneficiaryQuery/types';
import { queryClient, useApiMutation } from 'theSource/utils';
import { useConfigurationQuery } from 'theSource/queries';
import { ROUTES } from 'enrollment/app/routes';
import { useUnumIndexQuery } from 'enrollment/queries/useUnumIndexQuery';
import { PSBannerWrapper } from 'enrollment/components/Shared';
import { setAllBeneficiaryData } from 'enrollment/features/AddYourBeneficiaries/services';
import { GET_UNUM_GET_PLANS_KEY } from 'enrollment/queries/useUnumGetPlansQuery/utils';

interface BenefitDataState {
  state: {
    currentBenefitCategory: string;
    benefitData?: BenefitPlansDto;
    pendedBenefitData?: BenefitPlansDto;
    contextLookup?: string;
    fromReview?: boolean;
    benefitId?: number;
    preElectedPlan?: boolean;
    currentCategory?: string;
  };
}

export const UnumApplication = () => {
  const { t } = useTranslation('enrollment.plugins');

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation() as BenefitDataState;
  const benefit = location.state?.benefitData?.benefit;
  const fromReviewPage = location.state?.fromReview || false;
  const enrollmentContext = useSelector(selectEnrollmentContext);

  const { configuration } = useConfigurationQuery();
  const apiHost = configuration?.apiHost;

  const {
    unumApplicationData,
    isLoadingUnumApplication,
    isFetchingUnumApplication,
  } = useUnumApplicationQuery({
    orgBenefitId: Number(benefit?.org_benefit_id),
    enrollmentContext: enrollmentContext,
  });

  const { data: index } = useUnumIndexQuery({
    enabled: Boolean(apiHost && benefit?.org_benefit_id),
    lumosApiHost: apiHost || '',
    enrollmentContext: enrollmentContext,
    orgBenefitId: Number(benefit?.org_benefit_id),
  });

  const surveyQuestions =
    unumApplicationData?.data.application.Applicants.Applicant || [];
  const applicationDocuments =
    unumApplicationData?.data.application.Documents.Document || [];
  const statement = unumApplicationData?.data.application.Statements.Statement;
  const signatures = unumApplicationData?.data.application.Signatures;
  const beneficiariesRequired =
    unumApplicationData?.data.application.Applicants.Applicant.find(
      (applicant) => applicant.BeneficiariesSupported === 'Y'
    );

  const [surveyResponses, setSurveyResponses] =
    useState<UnumApplicationSurveyResponsesDto>({
      questions: {},
      question_answer_details: {},
    });
  const [documentResponses, setDocumentResponses] =
    useState<UnumApplicationRequestDto>({ documents: {} });
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [hasErrors, setHasErrors] = useState('');
  const [optionIndex, setOptionIndex] = useState(0);
  const [signatureSigned, setSignatureSigned] = useState<string | undefined>(
    undefined
  );
  const [totalAllocation, setTotalAllocation] = useState(0);

  const { mutate: UnumExitAppRequest } = useApiMutation<
    UnumExitAppResponseDto,
    ErrorResponseDto,
    UnumApplicationRequestDto
  >({
    queryKey: PUT_UNUM_SUBMIT_APPLICATION,
    method: 'put',
    endpoint: `${apiHost}/v2/self_service/plugins/unum/submit_application/${Number(benefit?.org_benefit_id)}`,
    onSuccess: (response) => {
      queryClient.refetchQueries(
        `unum-application-${String(benefit?.org_benefit_id)}`
      );
      queryClient.invalidateQueries(
        `${GET_UNUM_GET_PLANS_KEY}-${enrollmentContext}-${index?.data.id}`
      );
      if (response.data && !response.data.alert) {
        setHasErrors('');
        dispatch(setAllBeneficiaryData([]));
        switch (response.data.redirect.action) {
          case 'index':
            navigate(-1);
            break;
          case 'exit':
            fromReviewPage
              ? navigate(`/enrollment/${ROUTES.enrollment.review.path}`)
              : navigate(`/enrollment/${ROUTES.enrollment.benefits.path}`);
            break;
          case 'application':
            navigate(`/enrollment/${ROUTES.enrollment.benefits.path}`);
            break;
          default:
            break;
        }
      } else if (response.data.alert) {
        setHasErrors(response.data.alert);
      }
    },
    onError: () => {
      queryClient.refetchQueries(
        `unum-application-${String(benefit?.org_benefit_id)}`
      );
    },
    showSuccessSnackbar: false,
  });
  // uses selector because beneficiaries are stored in redux due do sharing component with beneficiaries page
  const beneficiaries: { [key: string]: BeneficiariesDto[] }[] = useSelector(
    (state: any) => state?.enrollment?.beneficiary?.beneficiaryData
  );

  const handleSubmit = () => {
    const payload = {
      unum: {},
      unum_benefit_type: index?.data?.unum_benefit_type,
    };
    formatApplicationQuestionPayload(surveyResponses, payload);
    formatHeightQuestionPayload(surveyResponses, payload, surveyQuestions);
    formatApplicationDocumentsPayload(documentResponses, payload);
    formatApplicationBeneficiariesPayload(beneficiaries, payload);
    formatApplicationSignedPayload(signatureSigned, payload);
    formatApplicationOptionsPayload(hasErrors, optionIndex, payload);

    UnumExitAppRequest(payload);
  };

  useEffect(() => {
    if (unumApplicationData) {
      setSurveyResponses(initalizeResponses(surveyQuestions));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unumApplicationData]);

  useEffect(() => {
    const hasErrorsAns = hasErrors === '0' || hasErrors === '';
    const allDocsChecked = Object.keys(documentResponses.documents).every(
      (key) => {
        return documentResponses.documents[key] === 'Y';
      }
    );
    const allSignaturesChecked = Boolean(signatureSigned);
    const totalAllocHundred = beneficiariesRequired
      ? totalAllocation === 100
      : true;

    if (
      hasErrorsAns &&
      allDocsChecked &&
      allSignaturesChecked &&
      totalAllocHundred
    ) {
      setSubmitDisabled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasErrors, documentResponses, signatureSigned, totalAllocation]);

  if (isLoadingUnumApplication || isFetchingUnumApplication) {
    return (
      <Skeleton
        maxHeight={32}
        maxWidth={64}
        data-testid="PostElectionApplication-loading-page"
        role="status"
        aria-label="Loading..."
      />
    );
  }

  if (!unumApplicationData) {
    return null;
  }

  return (
    <InternalPage
      testId="PostElectionApplication-page"
      CustomActionComponent={
        <BackLink className="my-3 mt-md-5 mb-md-2 mt-lg-0 pt-md-3_5 pt-lg-0" />
      }
      blockClasses="mt-md-5 pb-4 pt-2 px-4 px-md-0 p-lg-0 mt-lg-5"
    >
      <div>
        <h1>{unumApplicationData.data.page_title}</h1>
        <p className="semiBold">{t('unum.Application.surveyTitle')}</p>
        {hasErrors && (
          <div className="mb-3 mt-2">
            <PSBannerWrapper message={hasErrors} level="error" icon />
          </div>
        )}
        <UnumApplicationQuestions
          sections={unumApplicationData.data.application.Sections.Section}
          questions={surveyQuestions}
          responses={surveyResponses}
          setResponses={setSurveyResponses}
        />

        {beneficiariesRequired && (
          <UnumApplicationBeneficiaries
            data={unumApplicationData}
            setTotalAllocation={setTotalAllocation}
          ></UnumApplicationBeneficiaries>
        )}

        {applicationDocuments.length > 0 && (
          <UnumApplicationDocuments
            documents={applicationDocuments}
            responses={documentResponses}
            setResponses={setDocumentResponses}
            surveyQuestions={surveyQuestions}
            surveyResponses={surveyResponses}
          ></UnumApplicationDocuments>
        )}
        {statement && (
          <UnumApplicationStatement
            statement={statement}
          ></UnumApplicationStatement>
        )}
        {hasErrors && (
          <UnumApplicationOptions
            orgBenefitId={Number(benefit?.org_benefit_id)}
            optionIndex={optionIndex}
            setOptionIndex={setOptionIndex}
          />
        )}
        {signatures && signatures.length > 0 && (
          <UnumApplicationSignatures
            signatures={signatures}
            orgBenefitId={String(benefit?.org_benefit_id)}
            signatureSigned={signatureSigned}
            setSignatureSigned={setSignatureSigned}
          ></UnumApplicationSignatures>
        )}
        <PsButtonTuner
          label={t('unum.Application.completePurchase')}
          buttonId="completePurchase"
          level="primary"
          disabled={submitDisabled}
          onClick={handleSubmit}
        />
      </div>
    </InternalPage>
  );
};
